<template>
  <div>
    <div class="d-flex align-center">
      <h2>{{ $t('guidance.formTemplateSettings') }}</h2>
      <v-spacer />
      <v-btn
        @click="openEditDialog()"
        :disabled="loading"
        color="primary"
        variant="flat"
      >
        <v-icon start>mdi-plus</v-icon>
        {{ $t('common.create') }}
      </v-btn>
    </div>

    <!-- table -->
    <div class="mt-6">
      <v-data-table
        :headers="headers"
        :items="formResponse ? formResponse.data : []"
        :loading="loading"
        hide-default-footer
        :items-per-page="-1"
      >
        <!-- type -->
        <template v-slot:[`item.type`]="{ item }">
          {{ getPlanText(item.type) }}
        </template>

        <!-- createdAt -->
        <template v-slot:[`item.createdAt`]="{ item }">
          {{ formatDate(item.createdAt) }}
        </template>

        <!-- edit -->
        <template v-slot:[`item.edit`]="{ item }">
          <v-btn
            @click="openEditDialog(item)"
            :disabled="loading"
            color="primary"
            variant="outlined"
            small
          >
            {{ $t('common.edit') }}
          </v-btn>
        </template>

        <!-- delete -->
        <template v-slot:[`item.delete`]="{ item }">
          <v-btn
            @click="deleteForm(item.id)"
            :disabled="loading"
            color="error"
            small
            variant="flat"
          >
            {{ $t('common.delete') }}
          </v-btn>
        </template>
      </v-data-table>
    </div>

    <!-- dialog -->
    <v-dialog v-model="editDialog" max-width="700px">
      <v-card>
        <v-card-title>{{ $t('guidance.formItems') }}</v-card-title>
        <v-card-text>
          <v-container>
            <v-row align="center">
              <v-col cols="6"> {{ $t('guidance.templateName') }}* </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="input.name"
                  variant="outlined"
                  hide-details=""
                  density="compact"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row align="center">
              <v-col cols="6"> {{ $t('guidance.type') }}* </v-col>
              <v-col cols="6">
                <v-select
                  v-model="input.type"
                  :items="planItems"
                  variant="outlined"
                  hide-details=""
                  density="compact"
                ></v-select>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12"> {{ $t('guidance.formItems') }} </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-table>
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th>{{ $t('guidance.order') }}</th>
                        <th>{{ $t('guidance.itemName') }}</th>
                        <th>{{ $t('common.delete') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(item, i) in input.itemList" :key="i">
                        <td>{{ i + 1 }}</td>
                        <td style="width: 60%">
                          <v-select
                            v-model="item.itemId"
                            :items="planFormItems"
                            variant="outlined"
                            density="compact"
                            hide-details=""
                          ></v-select>
                        </td>
                        <td>
                          <v-btn
                            @click="deleteItemInItemList(i)"
                            color="error"
                            variant="outlined"
                            small
                          >
                            {{ $t('common.delete') }}
                          </v-btn>
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-table>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6">
                <v-btn
                  @click="AddItemToItemList"
                  :disabled="loading"
                  color="primary"
                  variant="outlined"
                  small
                >
                  <v-icon start>mdi-plus</v-icon>
                  {{ $t('guidance.addItem') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-container>
            <v-row>
              <v-btn @click="editDialog = false" color="grey" text>
                {{ $t('common.cancel') }}
              </v-btn>
              <v-spacer />
              <v-btn
                v-if="isUpdate"
                @click="updateForm"
                :disabled="loading"
                color="primary"
                variant="flat"
              >
                {{ $t('common.save') }}
              </v-btn>
              <v-btn
                v-else
                @click="createForm"
                :disabled="loading"
                color="primary"
                variant="flat"
              >
                {{ $t('common.create') }}
              </v-btn>
            </v-row>
          </v-container>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

,
<script lang="ts">
import { defineComponent } from "vue";
import ApiGuidance from "@/api/ApiGuidance";
import {
  GuidancePlanFormItemCategoryItems,
  GuidancePlanTypeItems,
} from "@/constants/guidance";
import LocalDataService from "@/service/LocalDataService";
import {
  GuidancePlanCategoryType,
  NurseryGuidancePlanFormDetail,
  NurseryGuidancePlanFormItemListResponse,
  NurseryGuidancePlanFormListResponse,
} from "chaild-api/lib";
import dayjs from "dayjs";

type ItemList = {
  itemId: string;
  order?: number; // orderは送信時に算出する
};

export default defineComponent({
  name: "GuidancePlanFormList",

  async mounted() {
    await this.listForm();
    await this.listFormItem();
  },

  data() {
    return {
      loading: false,
      editDialog: false,
      planItems: GuidancePlanTypeItems,
      categoryItems: GuidancePlanFormItemCategoryItems,
      headers: [
        { title: this.$t('guidance.templateName'), key: "name" },
        { title: this.$t('guidance.type'), key: "type" },
        { title: this.$t('guidance.createdAt'), key: "createdAt" },
        { title: this.$t('common.edit'), key: "edit" },
        { title: this.$t('guidance.duplicate'), key: "duplicate" },
        { title: this.$t('common.delete'), key: "delete" },
      ],
      searchFilters: {
        limit: 2000,
        skip: 0,
        type: "" as GuidancePlanCategoryType,
      },
      input: {
        name: "",
        type: "" as GuidancePlanCategoryType,
        itemList: [] as ItemList[],
        formId: 0,
      },
      /**
       * 帳票
       */
      formResponse: null as null | NurseryGuidancePlanFormListResponse,
      /**
       * 帳票項目
       */
      itemResponse: null as null | NurseryGuidancePlanFormItemListResponse,
    };
  },

  computed: {
    isUpdate: function (): boolean {
      return !!this.input.formId;
    },
    planFormItems: function (): any[] {
      if (this.itemResponse) {
        return this.itemResponse.data.map((i) => {
          return {
            text: i.name,
            value: i.id,
          };
        });
      } else {
        return [];
      }
    },
  },

  methods: {
    async listForm() {
      this.loading = true;
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        const response = await ApiGuidance.listPlanForm({
          nurseryId,
          ...this.searchFilters,
        }).catch(() => (this.loading = false));
        if (response) {
          this.formResponse = response;
        }
      }
      this.loading = false;
    },
    async createForm() {
      this.loading = true;
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        /**
         * orderを計算
         */
        this.input.itemList = this.calcItemListOrder();
        const response = await ApiGuidance.createPlanForm({
          nurseryId,
          ...this.input,
        }).catch(() => (this.loading = false));
        if (response) {
          this.editDialog = false;
          this.clearInput();
          await this.listForm();
        }
      }
      this.loading = false;
    },
    async updateForm() {
      this.loading = true;
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        /**
         * orderを計算
         */
        this.input.itemList = this.calcItemListOrder();
        const response = await ApiGuidance.updatePlanForm({
          nurseryId,
          ...this.input,
        }).catch(() => (this.loading = false));
        if (response) {
          this.editDialog = false;
          this.clearInput();
          await this.listForm();
        }
      }
      this.loading = false;
    },
    async deleteForm(formId: number) {
      const answer = confirm(this.$t('guidance.deleteConfirm'));
      if (answer) {
        this.loading = true;
        const nurseryId = LocalDataService.getNurseryId();
        if (nurseryId) {
          const response = await ApiGuidance.deletePlanForm({
            nurseryId,
            formId,
          }).catch(() => (this.loading = false));
          if (response) {
            alert(this.$t('guidance.deletedSuccessfully'));
            await this.listForm();
          }
        }
      }
      this.loading = false;
    },
    async listFormItem() {
      this.loading = true;
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        const response = await ApiGuidance.listPlanFormItem({
          nurseryId,
          ...this.searchFilters,
        }).catch(() => (this.loading = false));
        if (response) {
          this.itemResponse = response;
        }
      }
      this.loading = false;
    },
    openEditDialog(value?: NurseryGuidancePlanFormDetail) {
      if (value) {
        this.setInput(value);
      } else {
        this.clearInput();
      }
      this.editDialog = true;
    },
    clearInput() {
      this.input = {
        name: "",
        type: "" as GuidancePlanCategoryType,
        itemList: [] as ItemList[],
        formId: 0,
      };
    },
    setInput(value: NurseryGuidancePlanFormDetail) {
      this.input = {
        name: value.name,
        formId: value.id,
        type: value.type,
        itemList: value.itemList.map((item: any) => {
          return {
            itemId: item.item.id,
          };
        }),
      };
    },
    /**
     * ItemList.push
     */
    AddItemToItemList() {
      this.input.itemList.push({
        itemId: "",
      });
    },
    /**
     * itemList.delete
     */
    deleteItemInItemList(order: number) {
      this.input.itemList.splice(order, 1);
    },
    /**
     * calc order
     */
    calcItemListOrder(): ItemList[] {
      return this.input.itemList.map((item, index) => {
        return {
          itemId: item.itemId,
          order: index + 1,
        };
      });
    },
    getPlanText(value: string): string {
      const item = this.planItems.find((p) => p.value == value);
      return item?.text || "";
    },
    getCategryText(value: string): string {
      const item = this.categoryItems.find((c) => c.value == value);
      return item?.text || "";
    },
    formatDate(value: string): string {
      if (!value) {
        return "";
      }
      return dayjs(value).format("DD/MM/YYYY");
    },
  },
});
</script>
